/*
  Mobile sticky buy bar.

  Deliberately owns no cart logic. The bar's button clicks the real
  [name="add"] in the product form, so variant selection, quantity, the cart
  drawer and the sold-out state all come from code that already works. A second
  form here is how these bars drift out of sync with the variant picker.
*/

if (!customElements.get('ws-sticky-buy')) {
  customElements.define(
    'ws-sticky-buy',
    class WsStickyBuy extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.priceSlot = this.querySelector('[data-ws-sticky-price]');
        this.thumb = this.querySelector('[data-ws-sticky-thumb]');
        this.cta = this.querySelector('[data-ws-sticky-add]');
        this.label = this.querySelector('[data-ws-sticky-label]');

        this.productInfo = document.querySelector(`product-info[data-section="${this.sectionId}"]`);
        if (!this.productInfo) return;

        this.realButtons = this.productInfo.querySelector('.product-form__buttons');
        if (!this.realButtons) return;

        this.hidden = false;
        this.syncFrom(document);
        this.measure();
        this.watchButtons();
        this.watchVariant();

        this.cta.addEventListener('click', this.onClick.bind(this));
        this.resizeHandler = this.measure.bind(this);
        window.addEventListener('resize', this.resizeHandler);
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        if (this.unsubscribe) this.unsubscribe();
        if (this.resizeHandler) window.removeEventListener('resize', this.resizeHandler);
        document.body.classList.remove('ws-sticky-buy-active');
      }

      get realButton() {
        return (
          document.getElementById(`ProductSubmitButton-${this.sectionId}`) ||
          this.realButtons.querySelector('[name="add"]')
        );
      }

      /* Reveal only once the real Add to cart has left the viewport. */
      watchButtons() {
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => this.toggle(!entry.isIntersecting));
          },
          { rootMargin: '0px 0px -40px 0px' }
        );
        this.observer.observe(this.realButtons);
      }

      toggle(show) {
        this.classList.toggle('is-visible', show);
        document.body.classList.toggle('ws-sticky-buy-active', show);
      }

      measure() {
        document.documentElement.style.setProperty('--ws-sticky-buy-height', `${this.offsetHeight}px`);
      }

      watchVariant() {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const data = event.data || {};
          if (data.sectionId !== this.sectionId) return;

          if (data.html) {
            this.syncFrom(new DOMParser().parseFromString(data.html, 'text/html'));
          }
          this.syncThumb(data.variant);
          /* The real button is re-rendered by Dawn after this fires. */
          requestAnimationFrame(() => {
            this.syncButton();
            this.measure();
          });
        });
      }

      /* Copy Dawn's own price markup so sale and compare-at prices keep working. */
      syncFrom(source) {
        const price = source.querySelector(`#price-${this.sectionId}`);
        if (price && this.priceSlot) this.priceSlot.innerHTML = price.innerHTML;
        this.syncButton();
      }

      syncThumb(variant) {
        if (!this.thumb || !variant || !variant.featured_image) return;
        const src = variant.featured_image.src;
        if (!src) return;
        this.thumb.src = `${src.split('?')[0]}?width=128`;
      }

      /* Mirror whatever the real button currently says and whether it is usable. */
      syncButton() {
        const real = this.realButton;
        if (!real || !this.cta) return;

        const disabled = real.hasAttribute('disabled') || real.getAttribute('aria-disabled') === 'true';
        this.cta.disabled = disabled;
        this.cta.setAttribute('aria-disabled', disabled ? 'true' : 'false');

        /* Dawn puts the label in a single direct-child span; the spinner is a div.
           The whole-product-unavailable branch has no span, so fall back to the
           button itself. */
        const text = real.querySelector(':scope > span') || real;
        const copy = text.textContent.trim();
        if (copy && this.label) this.label.textContent = copy;
      }

      onClick(event) {
        event.preventDefault();
        const real = this.realButton;
        if (!real || this.cta.disabled) return;
        real.click();
      }
    }
  );
}
